import React, { useState } from 'react';
import { DefaultValues, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import CompanySelector from '@/components/QuerySelectors/CompanySelector';
import { ComboboxOption } from '@/components/Combobox';
import InlineInput from '@/components/InlineInput';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { NPS_FIXED_QUESTION, NPS_SCALE_DESCRIPTION } from '@/modules/nps/utils';

import { NpsCampaignFormSchema, npsCampaignFormSchema } from './schema';

interface IProps {
  companyOption?: ComboboxOption;
  defaultValues?: DefaultValues<NpsCampaignFormSchema>;
  isSubmitting?: boolean;
  onCancel?: () => void;
  onSubmit: SubmitHandler<NpsCampaignFormSchema>;
}

const NpsCampaignForm: React.FC<IProps> = ({
  companyOption,
  defaultValues,
  isSubmitting,
  onCancel,
  onSubmit,
}) => {
  const [selectedCompany, setSelectedCompany] = useState<
    ComboboxOption | undefined
  >(companyOption);
  const form = useForm<NpsCampaignFormSchema>({
    defaultValues: defaultValues ?? {
      name: '',
      companyId: 0,
      startAt: '',
      endAt: '',
      metricsVisibleToCompany: false,
      commentsVisibleToCompany: false,
      exportAvailableToCompany: false,
    },
    resolver: zodResolver(npsCampaignFormSchema),
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-4 py-4">
          <Alert>
            <AlertTitle>Pergunta fixa</AlertTitle>
            <AlertDescription>
              <p>{NPS_FIXED_QUESTION}</p>
              <p className="text-muted-foreground">{NPS_SCALE_DESCRIPTION}</p>
            </AlertDescription>
          </Alert>

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <InlineInput
                title="Nome"
                placeholder="Campanha NPS"
                disabled={isSubmitting}
                {...field}
              />
            )}
          />

          <FormField
            control={form.control}
            name="companyId"
            render={({ field }) => (
              <FormItem className="grid grid-cols-4 items-center gap-4">
                <FormLabel className="text-right">Empresa</FormLabel>
                <div className="col-span-3 flex flex-col items-baseline">
                  <FormControl>
                    <CompanySelector
                      value={
                        selectedCompany?.value === field.value
                          ? selectedCompany
                          : undefined
                      }
                      onChange={(option) => {
                        setSelectedCompany(option);
                        field.onChange(option?.value ?? 0);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="startAt"
            render={({ field }) => (
              <InlineInput
                title="Início"
                type="datetime-local"
                disabled={isSubmitting}
                {...field}
              />
            )}
          />

          <FormField
            control={form.control}
            name="endAt"
            render={({ field }) => (
              <InlineInput
                title="Fim"
                type="datetime-local"
                disabled={isSubmitting}
                {...field}
              />
            )}
          />

          <FormField
            control={form.control}
            name="metricsVisibleToCompany"
            render={({ field }) => (
              <FormItem className="grid grid-cols-4 items-center gap-4">
                <FormLabel className="text-right">Métricas</FormLabel>
                <div className="col-span-3 flex items-center gap-3">
                  <FormControl>
                    <Switch
                      checked={field.value}
                      disabled={isSubmitting}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormDescription>Visíveis para a empresa</FormDescription>
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="commentsVisibleToCompany"
            render={({ field }) => (
              <FormItem className="grid grid-cols-4 items-center gap-4">
                <FormLabel className="text-right">Comentários</FormLabel>
                <div className="col-span-3 flex items-center gap-3">
                  <FormControl>
                    <Switch
                      checked={field.value}
                      disabled={isSubmitting}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormDescription>Visíveis para a empresa</FormDescription>
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="exportAvailableToCompany"
            render={({ field }) => (
              <FormItem className="grid grid-cols-4 items-center gap-4">
                <FormLabel className="text-right">Exportação</FormLabel>
                <div className="col-span-3 flex items-center gap-3">
                  <FormControl>
                    <Switch
                      checked={field.value}
                      disabled={isSubmitting}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormDescription>Disponível para a empresa</FormDescription>
                </div>
              </FormItem>
            )}
          />
        </div>

        <DialogFooter>
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancelar
            </Button>
          )}
          <Button type="submit" loading={isSubmitting}>
            Salvar
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};

export default NpsCampaignForm;
